import { toPartialMatchRegex } from '../../../cdk/utils';
import type { InputAdapter } from './input.adapter';
import { isEditing } from './primitives';

const opts = {
  decimalPlaces: 2,
  locale: 'de-DE',
};

export type DecimalAdapterOpts = Partial<typeof opts>;

export class DecimalAdapter implements InputAdapter<number> {
  readonly #decimalSeparator: string;
  readonly #thousandSeparator: string;
  readonly #re: RegExp;
  readonly #config = opts;

  constructor(config: DecimalAdapterOpts = {}) {
    this.#config = {
      ...opts,
      ...config,
    };
    this.#decimalSeparator = (0.1)
      .toLocaleString(this.#config.locale)
      .charAt(1);
    this.#thousandSeparator = (1000)
      .toLocaleString(this.#config.locale)
      .charAt(1);
    const pattern = `^-?\\d*\\${this.#decimalSeparator}?\\d{0,${
      this.#config.decimalPlaces
    }}$`;
    this.#re = toPartialMatchRegex(new RegExp(pattern));
  }

  format(value: number | string | null | undefined): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'number') {
      return value.toLocaleString(this.#config.locale, {
        minimumFractionDigits: this.#config.decimalPlaces,
        maximumFractionDigits: this.#config.decimalPlaces,
      });
    }

    const v = value.replaceAll(this.#thousandSeparator, '');
    if (!v) {
      return '';
    }

    if (v === '-') {
      return v;
    }

    const [intPart, decPart = ''] = v.split(this.#decimalSeparator);
    const parsed = Number.parseInt(intPart);
    if (Number.isNaN(parsed)) {
      return '';
    }
    const formattedInt = parsed.toLocaleString(this.#config.locale);
    return `${formattedInt}${
      v.includes(this.#decimalSeparator) ? this.#decimalSeparator : ''
    }${decPart.substring(0, this.#config.decimalPlaces)}`;
  }

  parse(value: string | number | undefined | null): number | null {
    if (value === '' || value == null) {
      return null;
    }
    if (typeof value === 'number') {
      return +value.toFixed(this.#config.decimalPlaces);
    }

    const parsed = Number.parseFloat(
      value
        .replaceAll(this.#thousandSeparator, '')
        .replaceAll(this.#decimalSeparator, '.'),
    );
    if (Number.isNaN(parsed)) {
      return null;
    }
    return +parsed.toFixed(this.#config.decimalPlaces);
  }

  keydownHandler(e: KeyboardEvent) {
    const el = e.target as HTMLInputElement;
    if (isEditing(e)) {
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const val = el.value;
    const newVal = val.slice(0, start).concat(e.key, val.slice(end));
    if (this.#hasIncorrectSeparators(newVal)) {
      e.preventDefault();
      return;
    }
    const value = newVal.replaceAll(this.#thousandSeparator, '');
    if (!value) {
      return;
    }
    if (!this.#re.test(value)) {
      e.preventDefault();
    }

    const sepIndex = val.indexOf(this.#decimalSeparator);
    if (
      sepIndex > -1 &&
      start > sepIndex &&
      val.slice(sepIndex).length > this.#config.decimalPlaces
    ) {
      e.preventDefault();
    }
  }

  #hasIncorrectSeparators(value: string) {
    // Check for multiple decimal separators
    const decimalCount = (
      value.match(new RegExp(`\\${this.#decimalSeparator}`, 'g')) || []
    ).length;
    if (decimalCount > 1) {
      return true;
    }

    const sepIndex = value.lastIndexOf(this.#decimalSeparator);
    const groupIndex = value.lastIndexOf(this.#thousandSeparator);

    // Thousand separator at start or after decimal separator is invalid
    if (groupIndex === 0 || (sepIndex > -1 && groupIndex > sepIndex)) {
      return true;
    }

    // Check for consecutive thousand separators
    if (
      value.includes(`${this.#thousandSeparator}${this.#thousandSeparator}`)
    ) {
      return true;
    }

    // Validate thousand separator placement (groups of 3 digits)
    const parts = value.split(this.#decimalSeparator);
    const integerPart = parts[0] || '';

    // Check if value starts with minus
    const hasNegative = integerPart.startsWith('-');
    const intPartWithoutSign = hasNegative ? integerPart.slice(1) : integerPart;

    if (intPartWithoutSign.includes(this.#thousandSeparator)) {
      const groups = intPartWithoutSign.split(this.#thousandSeparator);

      // First group can be 1-3 digits
      if (groups[0].length === 0 || groups[0].length > 3) {
        return true;
      }

      // All other groups must be exactly 3 digits
      for (let i = 1; i < groups.length; i++) {
        if (groups[i].length !== 3) {
          return true;
        }
      }
    }

    return false;
  }
}
